import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { EXCLUSIVE_GAMES } from "../../data/site";
import { useGameLaunch } from "../../hooks/useGameLaunch";
import { useAuth } from "../../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROVIDER_LABEL = {
  aviator: "SPRIBE",
  "super-ace": "LUCKY365",
  "treasures-aztec": "PG SOFT",
  "fortune-gems": "JILI",
  "fortune-garuda": "JILI",
  "fortune-rabbit": "PG SOFT",
  "money-wheel": "JDB",
  "boxing-king": "JILI",
  "divas-ace": "YELLOW BAT",
  "super-ace-deluxe": "JILI",
  "money-coming": "JILI",
  "lucky-coming": "JILI",
};

// Alternate type keys used for filtering (case-insensitive).
const TYPE_ALIASES = {
  casino:  ["casino", "live casino", "live"],
  slots:   ["slots", "slot"],
  crash:   ["crash"],
  table:   ["table"],
  fishing: ["fishing", "fish"],
  arcade:  ["arcade"],
  lottery: ["lottery"],
};

// ---------- Favourites (per-user localStorage) ----------
const favKey = (userId) => `ml_favourites_${userId || "guest"}`;
const loadFavourites = (userId) => {
  try {
    const raw = localStorage.getItem(favKey(userId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};
const saveFavourites = (userId, ids) => {
  try {
    localStorage.setItem(favKey(userId), JSON.stringify(ids));
  } catch { /* ignore quota / private mode */ }
};

// Game tile — square (1:1) on sm+, slightly taller on mobile (3:4) for better
// phone visibility. `object-cover` handles any image aspect ratio cleanly.
// Robust image-fallback: if admin-set URL fails (broken / CORS / 404) we show
// the initials + title overlay so the tile never looks empty.
// Always-visible title pill at bottom on mobile so users know what they're tapping
// even when the game image doesn't contain the name.
export const BigGameTile = ({ game, onLaunch, isFavourite = false, onToggleFavourite = null }) => {
  const [imgOk, setImgOk] = useState(Boolean(game.img));
  useEffect(() => { setImgOk(Boolean(game.img)); }, [game.img]);
  const showFallback = !game.img || !imgOk;
  return (
  <button
    type="button"
    onClick={() => (onLaunch ? onLaunch(game.id, game.title, game.provider) : toast.info(`${game.title} — coming soon`))}
    data-testid={`biggame-${game.id}`}
    className="group relative rounded-xl sm:rounded-xl overflow-hidden bg-[#161e2a] ring-1 ring-white/[0.04] sm:ring-white/[0.06] hover:scale-[1.02] active:scale-[0.97] transition-all duration-300 aspect-[3/4] sm:aspect-square"
    style={{ boxShadow: "0 6px 20px -6px rgba(0,0,0,0.6), 0 2px 6px rgba(0,0,0,0.4)" }}
  >
    {/* Gradient fallback layer — always rendered, sits BEHIND the image */}
    <div
      aria-hidden
      className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-2 text-center"
      style={{
        background: `linear-gradient(135deg, ${game.color || "#19a55c"}33 0%, #0a1119 100%)`,
      }}
    >
      {showFallback && (
        <>
          <span
            className="font-display text-4xl sm:text-5xl leading-none"
            style={{ color: `${game.color || "#19a55c"}cc`, letterSpacing: "-0.06em" }}
          >
            {(game.title || "G").slice(0, 2).toUpperCase()}
          </span>
          <span className="text-[10px] sm:text-[11px] font-semibold text-white/70 line-clamp-2 leading-tight">
            {game.title}
          </span>
        </>
      )}
    </div>
    {game.img && imgOk && (
      <img
        src={game.img}
        alt={game.title}
        loading="lazy"
        referrerPolicy="no-referrer"
        className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-[1.06] transition-transform duration-500"
        style={{ filter: "saturate(1.05) contrast(1.02)" }}
        onError={() => setImgOk(false)}
      />
    )}
    <div
      aria-hidden
      className="absolute inset-0 rounded-xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
      style={{
        boxShadow: "inset 0 0 0 2px rgba(25,165,92,0.6), 0 0 30px rgba(25,165,92,0.4)",
      }}
    />
    {game.tag && (
      <div
        className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-[10px] font-bold text-white shadow-lg"
        style={{
          background:
            /hot/i.test(game.tag)
              ? "linear-gradient(135deg,#ef4444,#7a1e1e)"
              : /new/i.test(game.tag)
              ? "linear-gradient(135deg,#19a55c,#0f5e34)"
              : "linear-gradient(135deg,#ffc933,#a37100)",
        }}
      >
        {game.tag}
      </div>
    )}
    {/* Favourite heart — top-right corner. Stops event propagation so tap
        doesn't also launch the game. */}
    {onToggleFavourite && (
      <span
        role="button"
        tabIndex={0}
        data-testid={`fav-${game.id}`}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onToggleFavourite(game.id); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onToggleFavourite(game.id); } }}
        aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
        className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 h-8 w-8 sm:h-9 sm:w-9 rounded-full grid place-items-center bg-black/55 hover:bg-black/75 backdrop-blur-sm transition-transform active:scale-90 cursor-pointer"
        style={{ boxShadow: "0 4px 10px rgba(0,0,0,0.5)" }}
      >
        <Heart
          size={16}
          fill={isFavourite ? "#c89b3c" : "transparent"}
          stroke={isFavourite ? "#c89b3c" : "#fff"}
          strokeWidth={2}
        />
      </span>
    )}
  </button>
  );
};

// Sports special card - no game art; cricket icon-style cards
const SportCard = ({ s }) => (
  <Link
    to="/sports"
    data-testid={`sport-card-${s.id}`}
    className="group relative rounded-xl overflow-hidden bg-[#161e2a] hover:scale-[1.02] transition-all aspect-square flex flex-col items-center justify-center gap-2 p-4 border border-white/5 hover:border-[#ffc933]/40"
  >
    <img src={s.icon} alt={s.label} className="h-12 w-12 sm:h-16 sm:w-16" />
    <span className="text-sm font-bold text-white">{s.label}</span>
    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-bold flex items-center gap-1">
      <span className="h-1 w-1 rounded-full bg-red-500 animate-pulse" /> LIVE
    </span>
  </Link>
);

// Returns true if game `g` belongs to the given tab id ("popular", "casino", ...).
// Checks the new `categories` array first (lowercase slugs), then falls back to the
// legacy `type` field via TYPE_ALIASES so existing games keep working.
const gameMatchesTab = (g, tabId) => {
  const tab = String(tabId || "").toLowerCase();
  const cats = (g.categories || []).map((c) => String(c || "").toLowerCase());
  if (cats.includes(tab)) return true;
  const aliases = (TYPE_ALIASES[tab] || [tab]).map((s) => s.toLowerCase());
  if (aliases.includes(String(g.type || "").toLowerCase())) return true;
  return false;
};

// Master grid that switches based on active tab.
// Each tab now filters games by the multi-category `categories` array
// (with fallback to legacy `type`). A single game can appear in many tabs.
export const HomeGameGrid = ({ activeTab = "popular", sources = {} }) => {
  const { launchGame, modal: launchModal } = useGameLaunch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id || user?._id || user?.username || null;

  const [allGames, setAllGames] = useState(null); // null = loading
  const [favourites, setFavourites] = useState(() => loadFavourites(userId));

  // Re-load favourites when the logged-in user changes (login / logout)
  useEffect(() => {
    setFavourites(loadFavourites(userId));
  }, [userId]);

  // If the user navigates to the favourite tab while NOT logged in, send them
  // straight to the login page (per product requirement).
  useEffect(() => {
    if (activeTab === "favourite" && !user) {
      toast.info("Please log in to view your favourites");
      navigate("/login");
    }
  }, [activeTab, user, navigate]);

  const toggleFavourite = useCallback((gameId) => {
    if (!user) {
      toast.info("Please log in to save favourites");
      navigate("/login");
      return;
    }
    setFavourites((prev) => {
      const has = prev.includes(gameId);
      const next = has ? prev.filter((id) => id !== gameId) : [...prev, gameId];
      saveFavourites(userId, next);
      toast.success(has ? "Removed from favourites" : "Added to favourites");
      return next;
    });
  }, [user, userId, navigate]);

  useEffect(() => {
    let cancelled = false;
    let lastFetchAt = 0;
    let activeCtrl = null;
    let activeTimeoutId = null;
    const MIN_INTERVAL = 30000; // throttle re-fetches to once per 30s
    const FETCH_TIMEOUT_MS = 12000; // abort if backend takes too long
    const reload = (force = false) => {
      const now = Date.now();
      if (!force && now - lastFetchAt < MIN_INTERVAL) return;
      lastFetchAt = now;
      // Cancel any previous in-flight fetch so a stuck one cannot leave the
      // grid skeletons forever — the new attempt always wins.
      if (activeCtrl) { try { activeCtrl.abort(); } catch { /* ignore */ } }
      if (activeTimeoutId) clearTimeout(activeTimeoutId);
      const ctrl = new AbortController();
      activeCtrl = ctrl;
      activeTimeoutId = setTimeout(() => {
        try { ctrl.abort(); } catch { /* ignore */ }
      }, FETCH_TIMEOUT_MS);
      fetch(`${BACKEND_URL}/api/public/games?_t=${now}`, {
        cache: "no-store",
        signal: ctrl.signal,
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((items) => {
          if (cancelled) return;
          if (activeTimeoutId) { clearTimeout(activeTimeoutId); activeTimeoutId = null; }
          // Only replace the list if we got a non-empty array back.
          // On error / empty response we KEEP the existing games so the user
          // never sees the grid blank out on flaky mobile networks.
          if (Array.isArray(items) && items.length > 0) {
            setAllGames(items);
          } else if (Array.isArray(items)) {
            // legitimate empty list — only set on the very first load
            setAllGames((prev) => (prev === null ? [] : prev));
          }
        })
        .catch(() => {
          // Network error / abort — keep the previously loaded games visible.
          // If this was the very first attempt and we still have nothing,
          // show an empty grid so the UI doesn't stay stuck on skeletons.
          if (activeTimeoutId) { clearTimeout(activeTimeoutId); activeTimeoutId = null; }
          if (!cancelled) {
            setAllGames((prev) => (prev === null ? [] : prev));
          }
        });
    };
    reload(true);
    // Auto-refresh policy (per user request):
    // No more focus / visibilitychange-driven reloads. The HomePage game grid
    // used to silently re-fetch every time the user switched tabs back, which
    // caused the visible "page refreshing by itself" complaint. Now we only
    // re-fetch when the network reconnects after a real outage. New games
    // added by admin will appear on the next manual reload or navigation —
    // that's an acceptable trade-off for a stable, non-flickering UI.
    const onOnline = () => reload(true);
    window.addEventListener("online", onOnline);
    return () => {
      cancelled = true;
      if (activeCtrl) { try { activeCtrl.abort(); } catch { /* ignore */ } }
      if (activeTimeoutId) clearTimeout(activeTimeoutId);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  const games = useMemo(() => {
    if (allGames === null) return null;
    let filtered;
    if (activeTab === "favourite") {
      const favSet = new Set(favourites);
      filtered = allGames.filter((g) => favSet.has(g.id));
    } else {
      filtered = allGames.filter((g) => gameMatchesTab(g, activeTab));
    }
    // Sort: badged first (Hot/New/Top surface to top), then `order` asc, then created_at desc.
    return filtered.sort((a, b) => {
      const aBadged = a.badge ? 1 : 0;
      const bBadged = b.badge ? 1 : 0;
      if (aBadged !== bBadged) return bBadged - aBadged;
      const ao = a.order ?? 0;
      const bo = b.order ?? 0;
      if (ao !== bo) return ao - bo;
      return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    });
  }, [allGames, activeTab, favourites]);

  const loading = games === null;

  const renderTiles = () => {
    if (activeTab === "sports") {
      // Sports kept as icon tiles (no game art for sports books)
      const sportItems = sources.SPORTS_TABS || [];
      return sportItems.map((s) => <SportCard key={s.id} s={s} />);
    }
    if (loading) {
      return Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="aspect-square rounded-xl bg-[#161e2a] border border-white/5 animate-pulse" />
      ));
    }
    if (!games || games.length === 0) {
      // Empty state — admin hasn't added any game to this tab yet.
      // Popular tab uses the same message; admin must explicitly check
      // "Popular (Home)" on a game to surface it here.
      return (
        <div className="col-span-full py-10 text-center text-white/55 text-sm">
          {activeTab === "popular"
            ? "No games marked Popular yet. Admin can check 'Popular (Home)' on any game to feature it here."
            : activeTab === "favourite"
            ? "You haven't favourited any games yet. Tap the heart on any game tile to save it here."
            : "No games added yet in this category."}
        </div>
      );
    }
    return games.map((g) => (
      <BigGameTile
        key={g.id}
        game={{ id: g.id, title: g.name, img: g.image, tag: g.badge || "", provider: g.provider }}
        onLaunch={launchGame}
        isFavourite={favourites.includes(g.id)}
        onToggleFavourite={toggleFavourite}
      />
    ));
  };

  return (
    <>
      <div
        data-testid="home-game-grid"
        className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8 gap-2 sm:gap-3"
      >
        {renderTiles()}
      </div>
      {launchModal}
    </>
  );
};
